/**
 * Calendar Modal
 * Handles modal functionality for adding events to calendar
 */

class CalendarModal {
	constructor() {
		this.modal = document.getElementById( 'calendar-modal' );
		this.overlay = null;
		this.closeBtn = null;
		this.detailsBtn = null;
		this.timeOnlyBtn = null;
		this.triggerBtns = [];
		this.currentMeetingData = null;

		if ( this.modal ) {
			this.init();
		}
	}

	init() {
		// Get all calendar button triggers
		this.triggerBtns = Array.from( document.querySelectorAll( '.meeting-card__btn-calendar' ) );

		// Get modal elements
		this.overlay = this.modal.querySelector( '.calendar-modal__overlay' );
		this.closeBtn = this.modal.querySelector( '.calendar-modal__close' );
		this.detailsBtn = this.modal.querySelector( '.calendar-modal__btn-details' );
		this.timeOnlyBtn = this.modal.querySelector( '.calendar-modal__btn-time-only' );

		// Bind events
		this.bindEvents();
	}

	bindEvents() {
		// Click on calendar buttons to open modal
		this.triggerBtns.forEach( ( btn ) => {
			btn.addEventListener( 'click', ( e ) => {
				e.preventDefault();

				// Get meeting data from button attributes
				this.currentMeetingData = {
					title: btn.getAttribute( 'data-meeting-title' ) || 'Meeting',
					date: btn.getAttribute( 'data-meeting-date' ) || '',
					startTime: btn.getAttribute( 'data-meeting-start' ) || '',
					endTime: btn.getAttribute( 'data-meeting-end' ) || '',
					description: btn.getAttribute( 'data-meeting-description' ) || '',
					location: btn.getAttribute( 'data-meeting-location' ) || '',
					url: btn.getAttribute( 'data-meeting-url' ) || ''
				};

				this.open();
			} );
		} );

		// Close modal
		if ( this.closeBtn ) {
			this.closeBtn.addEventListener( 'click', () => this.close() );
		}

		if ( this.overlay ) {
			this.overlay.addEventListener( 'click', () => this.close() );
		}

		// Handle button actions
		if ( this.detailsBtn ) {
			this.detailsBtn.addEventListener( 'click', () => {
				this.handleCalendarAction( 'details' );
			} );
		}

		if ( this.timeOnlyBtn ) {
			this.timeOnlyBtn.addEventListener( 'click', () => {
				this.handleCalendarAction( 'timeonly' );
			} );
		}

		// ESC key to close
		document.addEventListener( 'keydown', ( e ) => {
			if ( e.key === 'Escape' && this.modal.classList.contains( 'calendar-modal--active' ) ) {
				this.close();
			}
		} );
	}

	open() {
		this.modal.classList.add( 'calendar-modal--active' );
		this.modal.setAttribute( 'aria-hidden', 'false' );
		document.body.classList.add( 'modal-open' );

		// Update modal message with meeting title
		const messageElement = this.modal.querySelector( '.calendar-modal__text' );
		if ( messageElement && this.currentMeetingData ) {
			messageElement.textContent = `Would you like to add details about ${this.currentMeetingData.title} in the calendar appointment?`;
		}
	}

	close() {
		this.modal.classList.remove( 'calendar-modal--active' );
		this.modal.setAttribute( 'aria-hidden', 'true' );
		document.body.classList.remove( 'modal-open' );
	}

	handleCalendarAction( action ) {
		if ( ! this.currentMeetingData ) {
			// eslint-disable-next-line no-console
			console.error( 'No meeting data available' );
			this.close();
			return;
		}

		// Generate ICS file
		const icsContent = this.generateICS( action );

		// Trigger download
		this.downloadICS( icsContent, this.currentMeetingData.title );

		// Close modal
		this.close();
	}

	generateICS( includeDetails ) {
		const data = this.currentMeetingData;

		// Parse date and times to create DateTime stamps
		const startDateTime = this.parseDateTime( data.date, data.startTime );

		// If no end time provided, assume 1 hour duration
		let endDateTime;
		if ( data.endTime ) {
			endDateTime = this.parseDateTime( data.date, data.endTime );
		} else if ( startDateTime ) {
			endDateTime = new Date( startDateTime.getTime() + ( 60 * 60 * 1000 ) ); // Add 1 hour
		}

		if ( ! startDateTime || ! endDateTime ) {
			// eslint-disable-next-line no-console
			console.error( 'Invalid date/time format' );
			return '';
		}

		const dtStamp = this.formatICSDateTime( new Date() );

		// Generate unique ID
		const uid = `meeting-${Date.now()}@chia.com`;

		// Build ICS content
		let icsLines = [
			'BEGIN:VCALENDAR',
			'VERSION:2.0',
			'PRODID:-//CHIA//Meeting Calendar//EN',
			'CALSCALE:GREGORIAN',
			'METHOD:PUBLISH',
			'BEGIN:VEVENT',
			`UID:${uid}`,
			`DTSTAMP:${dtStamp}`
		];

		// Add title
		icsLines.push( `SUMMARY:${this.escapeICSText( data.title )}` );

		// Include time only (specific start/end times)
		if ( includeDetails === 'timeonly' ) {
			const dtStart = this.formatICSDateTime( startDateTime );
			const dtEnd = this.formatICSDateTime( endDateTime );
			icsLines.push( `DTSTART:${dtStart}` );
			icsLines.push( `DTEND:${dtEnd}` );
		}

		// Include details (all-day event with description/location/url)
		if ( includeDetails === 'details' ) {
			// All-day event - just the date, no time
			const dateOnly = data.date.replace( /-/g, '' ); // Convert YYYY-MM-DD to YYYYMMDD
			icsLines.push( `DTSTART;VALUE=DATE:${dateOnly}` );
			icsLines.push( `DTEND;VALUE=DATE:${dateOnly}` );

			if ( data.description ) {
				icsLines.push( `DESCRIPTION:${this.escapeICSText( data.description )}` );
			}

			if ( data.location ) {
				icsLines.push( `LOCATION:${this.escapeICSText( data.location )}` );
			}

			if ( data.url ) {
				icsLines.push( `URL:${data.url}` );
			}
		}

		icsLines.push(
			'STATUS:CONFIRMED',
			'SEQUENCE:0',
			'END:VEVENT',
			'END:VCALENDAR'
		);

		return icsLines.join( '\r\n' );
	}

	parseDateTime( dateStr, timeStr ) {
		if ( ! dateStr || ! timeStr ) {
			return null;
		}

		try {
			// Expected format: dateStr = "YYYY-MM-DD", timeStr = "4:00 am" or "3:30 pm"
			const [ year, month, day ] = dateStr.split( '-' );

			// Parse time in "g:i a" format (e.g., "4:00 am")
			const timeMatch = timeStr.match( /(\d+):(\d+)\s*(am|pm)/i );

			if ( ! timeMatch ) {
				// eslint-disable-next-line no-console
				console.error( 'Invalid time format:', timeStr );
				return null;
			}

			let hours = parseInt( timeMatch[1], 10 );
			const minutes = parseInt( timeMatch[2], 10 );
			const meridiem = timeMatch[3].toLowerCase();

			// Convert to 24-hour format
			if ( meridiem === 'pm' && hours !== 12 ) {
				hours += 12;
			} else if ( meridiem === 'am' && hours === 12 ) {
				hours = 0;
			}

			// Create date in EST/EDT (America/New_York)
			return new Date( year, month - 1, day, hours, minutes, 0 );
		} catch ( error ) {
			// eslint-disable-next-line no-console
			console.error( 'Error parsing date/time:', error );
			return null;
		}
	}

	formatICSDateTime( date ) {
		// Format: YYYYMMDDTHHmmss
		const year = date.getFullYear();
		const month = String( date.getMonth() + 1 ).padStart( 2, '0' );
		const day = String( date.getDate() ).padStart( 2, '0' );
		const hours = String( date.getHours() ).padStart( 2, '0' );
		const minutes = String( date.getMinutes() ).padStart( 2, '0' );
		const seconds = String( date.getSeconds() ).padStart( 2, '0' );

		return `${year}${month}${day}T${hours}${minutes}${seconds}`;
	}

	escapeICSText( text ) {
		if ( ! text ) {
			return '';
		}

		// Escape special characters for ICS format
		return text
			.replace( /\\/g, '\\\\' )
			.replace( /;/g, '\\;' )
			.replace( /,/g, '\\,' )
			.replace( /\n/g, '\\n' );
	}

	downloadICS( icsContent, filename ) {
		// Create blob with ICS content
		const blob = new Blob( [ icsContent ], { type: 'text/calendar;charset=utf-8' } );

		// Create download link
		const link = document.createElement( 'a' );
		link.href = window.URL.createObjectURL( blob );
		link.download = `${this.sanitizeFilename( filename )}.ics`;

		// Trigger download
		document.body.appendChild( link );
		link.click();

		// Cleanup
		document.body.removeChild( link );
		window.URL.revokeObjectURL( link.href );
	}

	sanitizeFilename( filename ) {
		// Remove special characters and spaces from filename
		return filename
			.replace( /[^a-z0-9]/gi, '_' )
			.replace( /_+/g, '_' )
			.toLowerCase();
	}
}

// Initialize on DOM ready
if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', () => {
		new CalendarModal();
	} );
} else {
	new CalendarModal();
}

export default CalendarModal;
